import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { transactionApi } from "../services/api";

export interface YearlySummary {
  year: number;
  group_id: string;
  monthly_summaries: Array<{
    summary_id: string;
    group_id: string;
    month_year: string;
    total_income: number;
    total_expenses: number;
    net_balance: number;
    transaction_count: number;
    category_breakdown: { [key: string]: number };
    payer_breakdown: { [key: string]: number };
    created_at: string;
    updated_at: string;
  }>;
  yearly_totals: {
    total_income: number;
    total_expenses: number;
    net_balance: number;
    transaction_count: number;
  };
}

export const useYearlyData = (year?: number) => {
  const { user } = useAuth();
  const [yearlyData, setYearlyData] = useState<YearlySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);

  const loadYearlyData = useCallback(async () => {
    if (!user?.group_id || loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const targetYear = year || new Date().getFullYear();
      const data = await transactionApi.getYearlySummary(
        user.group_id!,
        targetYear
      );
      setYearlyData(data);
    } catch (err: any) {
      setError(err.message || "Failed to load yearly data");
      setYearlyData(null);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [user?.group_id, year]);

  useEffect(() => {
    loadYearlyData();
  }, [loadYearlyData]);

  return { yearlyData, loading, error, refresh: loadYearlyData };
};
